import {Component, EventEmitter, Input, OnInit, Output, TrackByFunction} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators, ɵFormGroupRawValue, ɵTypedOrUntyped} from '@angular/forms';
import {TrendStatisticResponse} from '@npck/af-frontend-api';
import {rxSpinner, Spinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {EMPTY, mergeMap, of} from 'rxjs';
import {AuthHolderService} from '../../../../../domain/auth-holder.service';
import {TrendStatisticForm} from './trend-statistic/trend-statistic.component';

@Component({
    selector: 'af-trend-statistics-step',
    templateUrl: './trend-statistics-step.component.html',
    styleUrls: ['trend-statistics-step.component.scss'],
    standalone: false
})
export class TrendStatisticsStepComponent implements OnInit {
    @Input() form: FormGroup<TrendStatisticsForm>;
    @Input() indexStartsFrom: number = 0;
    @Input() trendStatistics: Array<TrendStatisticResponse>;
    @Output() previousStep = new EventEmitter<void>();
    Spinner = Spinner;

    constructor(
        private formBuilder: FormBuilder,
        private dialogService: DialogService,
        private authHolderService: AuthHolderService,
    ) {
    }

    get showDeleteButton(): boolean {
        if(!this.authHolderService.hasAccess(['npck-admin'])) {
            return false;
        }
        const count = this.form?.controls?.trendStatistics?.controls?.length ?? 0;

        return count > 1 && !this.Spinner.isShown('initForms');
    }

    ngOnInit(): void {
        if (!this.form.controls?.trendStatistics) {
            this.form.addControl('trendStatistics', this.formBuilder.array([] as FormGroup<TrendStatisticForm>[]));
            this.form.addControl('deletedTrendStatistics', this.formBuilder.array([] as FormGroup<TrendStatisticForm>[]));
            this.trendStatistics.forEach(item => this.initializeForm(item));
        }
    }

    trackByTrendStatisticIndex: TrackByFunction<FormGroup<TrendStatisticForm>> = (index: number) => index;

    deleteTrendStatistic(index: number): void {
        const trendStatistic = this.trendStatistics[index];
        this.dialogService.openDialog({
            title: 'i18n_af_deleteTrendStatisticDialogTitle',
            subtitle: 'i18n_af_deleteTrendStatisticDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {id: `${trendStatistic.id}`},
        })
            .pipe(
                mergeMap(dialogResult => !dialogResult && EMPTY || of(dialogResult)
                    .pipe(rxSpinner(`deleteTrendStatistic_${index}`))),
            )
            .subscribe(() => {
                const rawForm = this.form.getRawValue();
                const deletedTrendStatisticValue = rawForm.trendStatistics[index];
                const deletedTrendStatisticControl = this.form.controls.trendStatistics?.at(index);
                if (!!deletedTrendStatisticValue.id) {
                    deletedTrendStatisticControl.controls.isDeleted.setValue(true);
                    this.trendStatistics.splice(index, 1);
                    this.form.controls.deletedTrendStatistics.push(deletedTrendStatisticControl);
                }
                this.form.controls.trendStatistics.removeAt(index);
            });
    }

    private initializeForm(trendStatistic: TrendStatisticResponse): void {
        const trendStatisticForm = this.buildTrendStatisticForm(trendStatistic);
        this.form.controls.trendStatistics.push(trendStatisticForm);
    }

    private buildTrendStatisticForm(trendStatistic: TrendStatisticResponse): FormGroup<TrendStatisticForm> {
        const trendStatisticForm = this.formBuilder.group<TrendStatisticForm>({});
        trendStatisticForm.addControl('id', new FormControl(trendStatistic?.id, [Validators.required]));
        trendStatisticForm.addControl('country', new FormControl(trendStatistic?.country, [Validators.required]));
        trendStatisticForm.addControl('region', new FormControl(trendStatistic?.region, [Validators.required]));
        trendStatisticForm.addControl('incidentCount', new FormControl(trendStatistic?.incidentCount + '', [Validators.required]));
        trendStatisticForm.addControl('uniqueVictimCount', new FormControl(trendStatistic?.uniqueVictimCount + '', [Validators.required]));
        trendStatisticForm.addControl('isDeleted', new FormControl(trendStatistic?.isDeleted));
        trendStatisticForm.addControl('periodRange', this.formBuilder.group({}));

        return trendStatisticForm;
    }
}

export type TrendStatisticsFormValue = ɵTypedOrUntyped<TrendStatisticsForm, ɵFormGroupRawValue<TrendStatisticsForm>, any>;

export interface TrendStatisticsForm {
    trendStatistics?: FormArray<FormGroup<TrendStatisticForm>>;
    deletedTrendStatistics?: FormArray<FormGroup<TrendStatisticForm>>;
}
